/**
 * Transform pre-rendered HTML to replace html-embed code blocks with actual
 * iframe elements. Server-serialized Tiptap content turns html-embed code
 * blocks into <pre><code class="html-embed ...">...</code></pre>. This
 * function parses those blocks and replaces them with embedded iframes.
 */

const VIDEO_SRC_RE = /https?:\/\/(?:www\.)?(?:youtube\.com\/embed\/|loom\.com\/embed\/)\S+/;

/**
 * Whether a src may load in the embed iframe. A javascript: URL runs with the
 * embedding page's origin and a relative path loads our own, so only absolute
 * https URLs pass; embedSandbox then decides whether the document keeps its
 * own origin. Mirrors isSafeEmbedSrc in
 * apps/web/components/docs/knowledge/html-embed-directive.ts.
 */
export function isSafeEmbedSrc(src: string): boolean {
  try {
    return new URL(src).protocol === "https:";
  } catch {
    return false;
  }
}

/** Video providers whose players need their own origin's storage to play. */
const SAME_ORIGIN_EMBED_HOSTS = [
  "www.youtube.com",
  "youtube.com",
  "www.youtube-nocookie.com",
  "www.loom.com",
  "loom.com",
];

/**
 * Sandbox for an embed iframe. `allow-same-origin` next to `allow-scripts`
 * lets a same-origin document remove its own sandbox, so it is granted only
 * to the video providers above. Mirrors embedSandbox in
 * apps/web/components/docs/knowledge/html-embed-directive.ts.
 */
export function embedSandbox(src?: string): string {
  const base = "allow-scripts allow-forms allow-popups";
  if (!src) return base;
  try {
    return SAME_ORIGIN_EMBED_HOSTS.includes(new URL(src).hostname)
      ? `${base} allow-same-origin`
      : base;
  } catch {
    return base;
  }
}

/** Escapes a value for an HTML attribute or text node in the string built below. */
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

interface ParsedDirective {
  src?: string;
  html?: string;
  height?: number;
  label?: string;
}

function parseDirective(content: string): ParsedDirective {
  const lines = content.split("\n");
  let src: string | undefined;
  let height: number | undefined;
  let label: string | undefined;
  let bodyStartIdx = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (trimmed === "") {
      bodyStartIdx = i + 1;
      continue;
    }
    const m = trimmed.match(/^(src|height|label)=(.*)$/);
    if (!m) break;
    const value = m[2].trim();
    if (m[1] === "src") src = isSafeEmbedSrc(value) ? value : undefined;
    else if (m[1] === "height") {
      const n = parseInt(value, 10);
      if (!Number.isNaN(n)) height = n;
    } else if (m[1] === "label") label = value || undefined;
    bodyStartIdx = i + 1;
  }

  const body = lines.slice(bodyStartIdx).join("\n").trim();

  if (body && !src) {
    if (!body.includes("\n") && isSafeEmbedSrc(body)) {
      return { src: body, height, label };
    }
    if (body.startsWith("<")) {
      return { src, html: body, height, label };
    }
  }

  return { src, height, label };
}

// SVG icons used in the static HTML path (no React / no lucide-react).
const MAXIMIZE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;
const MINIMIZE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;
const EXTERNAL_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;
const FILE_CODE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="m10 13-2 2 2 2"/><path d="m14 17 2-2-2-2"/></svg>`;

/**
 * Attach delegated click handlers for the fullscreen buttons rendered by
 * `buildIframeHtml`. Must be called from a React `useEffect` because
 * `<script>` tags inside `dangerouslySetInnerHTML` are never executed by
 * the browser.
 *
 * Safe to call multiple times — only the first call installs listeners.
 * Returns a cleanup function that removes them.
 */
export function attachHtmlEmbedFullscreen(): () => void {
  function closeOverlay(overlay: HTMLElement) {
    const prev = (overlay as any)._prevOverflow;
    overlay.remove();
    document.body.style.overflow = prev || "";
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      const overlay = document.querySelector<HTMLElement>("[data-embed-overlay]");
      if (overlay) closeOverlay(overlay);
    }
  }

  function onClick(e: MouseEvent) {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-embed-fullscreen]");
    if (!btn) return;

    e.preventDefault();
    const wrap = btn.closest<HTMLElement>("[data-embed-wrap]");
    if (!wrap) return;
    const iframe = wrap.querySelector("iframe");
    if (!iframe) return;

    let src = iframe.getAttribute("src") || "";
    const srcdoc = iframe.getAttribute("srcdoc") || "";
    const sandbox = iframe.getAttribute("sandbox") || "";
    const allow = iframe.getAttribute("allow") || "";
    const title = iframe.getAttribute("title") || "HTML embed";
    const label = wrap.getAttribute("data-embed-label") || title;
    const openSrc = wrap.getAttribute("data-embed-open-src") || "";

    if (src && /youtube\.com\/embed\//.test(src)) {
      src += (src.includes("?") ? "&" : "?") + "autoplay=1";
    }
    if (src && /loom\.com\/embed\//.test(src)) {
      src += (src.includes("?") ? "&" : "?") + "autoplay=1";
    }

    const overlay = document.createElement("div");
    overlay.setAttribute("data-embed-overlay", "");
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:60;display:flex;flex-direction:column;background:rgba(255,255,255,0.95);backdrop-filter:blur(4px)";
    (overlay as any)._prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const header = document.createElement("div");
    header.style.cssText =
      "display:flex;align-items:center;justify-content:space-between;gap:12px;padding:0 16px;height:48px;border-bottom:1px solid var(--border,#e5e5e5);background:var(--background,#fff);flex-shrink:0";

    const left = document.createElement("span");
    left.style.cssText =
      "display:inline-flex;align-items:center;gap:8px;font-size:14px;color:var(--muted-foreground,#6b7280);min-width:0";
    left.innerHTML = `${FILE_CODE_SVG}<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${label}</span>`;

    const right = document.createElement("div");
    right.style.cssText = "display:flex;align-items:center;gap:8px";

    if (openSrc) {
      const link = document.createElement("a");
      link.href = openSrc;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.cssText =
        "display:inline-flex;align-items:center;gap:4px;font-size:12px;color:var(--muted-foreground,#6b7280);text-decoration:none";
      link.innerHTML = `Open in new tab ${EXTERNAL_SVG}`;
      right.appendChild(link);
    }

    const closeBtn = document.createElement("button");
    closeBtn.setAttribute("aria-label", "Exit fullscreen");
    closeBtn.title = "Exit fullscreen (Esc)";
    closeBtn.style.cssText =
      "display:inline-flex;align-items:center;justify-content:center;height:32px;width:32px;border-radius:6px;border:0;background:none;color:var(--muted-foreground,#6b7280);cursor:pointer";
    closeBtn.innerHTML = MINIMIZE_SVG;
    closeBtn.addEventListener("click", () => closeOverlay(overlay));
    right.appendChild(closeBtn);

    header.appendChild(left);
    header.appendChild(right);

    const body = document.createElement("div");
    body.style.cssText = "flex:1;min-height:0;background:#fff;overflow:hidden";

    const fi = document.createElement("iframe");
    if (src) fi.src = src;
    if (srcdoc) fi.srcdoc = srcdoc;
    if (sandbox) fi.setAttribute("sandbox", sandbox);
    if (allow) fi.allow = allow;
    fi.title = `${title} (fullscreen)`;
    fi.style.cssText = "width:100%;height:100%;display:block;background:#fff;border:0";
    body.appendChild(fi);

    overlay.appendChild(header);
    overlay.appendChild(body);
    overlay.addEventListener("click", (ev) => {
      if (ev.target === overlay) closeOverlay(overlay);
    });
    document.body.appendChild(overlay);
  }

  document.addEventListener("keydown", onKeyDown);
  document.addEventListener("click", onClick);

  return () => {
    document.removeEventListener("keydown", onKeyDown);
    document.removeEventListener("click", onClick);
  };
}

function buildIframeHtml(parsed: ParsedDirective): string {
  const src = parsed.src && escapeHtml(parsed.src);
  const label = parsed.label && escapeHtml(parsed.label);
  const isVideo = Boolean(
    (parsed.src && VIDEO_SRC_RE.test(parsed.src)) ||
    (parsed.html && VIDEO_SRC_RE.test(parsed.html))
  );

  /** Fullscreen button HTML shared across all embed variants. */
  const fsBtn = `<button data-embed-fullscreen aria-label="Fullscreen" title="Fullscreen" style="display:inline-flex;align-items:center;justify-content:center;height:24px;width:24px;border-radius:6px;border:0;background:none;color:var(--muted-foreground,#6b7280);cursor:pointer">${MAXIMIZE_SVG}</button>`;

  /** Floating fullscreen button overlaid on video embeds (visible on hover via CSS). */
  const videoFsBtn = `<button data-embed-fullscreen aria-label="Fullscreen" title="Fullscreen" style="position:absolute;top:8px;right:8px;display:inline-flex;align-items:center;justify-content:center;height:32px;width:32px;border-radius:8px;border:0;background:rgba(0,0,0,0.6);color:#fff;cursor:pointer;opacity:0;transition:opacity 150ms">${MAXIMIZE_SVG}</button>`;

  /** Data attributes on the wrapper so the delegated handler can read them. */
  const wrapAttrs = `data-embed-wrap data-embed-label="${label ?? "HTML embed"}"${parsed.src ? ` data-embed-open-src="${src}"` : ""}`;

  /** CSS hover rule for video overlay button: show on parent hover. */
  const videoHoverStyle = `<style>[data-embed-wrap]:hover>[data-embed-fullscreen]{opacity:1!important}</style>`;

  if (isVideo && parsed.src) {
    return `${videoHoverStyle}<div ${wrapAttrs} style="position:relative;border-radius:8px;overflow:hidden;background:#000;margin:1rem 0">
      <iframe src="${src}" title="${label ?? "Video"}" style="aspect-ratio:16/9;width:100%;display:block;border:0" sandbox="${embedSandbox(parsed.src)}" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" allowfullscreen></iframe>
      ${videoFsBtn}
    </div>`;
  }

  if (isVideo && parsed.html) {
    const wrappedHtml = `<!DOCTYPE html><html><head><style>*{margin:0;padding:0;box-sizing:border-box}html,body{width:100%;height:100%;overflow:hidden}iframe{width:100%;height:100%;border:0}</style></head><body>${parsed.html}</body></html>`;
    return `${videoHoverStyle}<div ${wrapAttrs} style="position:relative;border-radius:8px;overflow:hidden;background:#000;margin:1rem 0">
      <iframe srcdoc="${escapeHtml(wrappedHtml)}" title="${label ?? "Video"}" style="aspect-ratio:16/9;width:100%;display:block;border:0" sandbox="allow-scripts allow-forms allow-popups" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" allowfullscreen></iframe>
      ${videoFsBtn}
    </div>`;
  }

  if (parsed.src) {
    const h = parsed.height ?? 520;
    return `<div ${wrapAttrs} style="border-radius:8px;overflow:hidden;border:1px solid var(--border,#e5e5e5);margin:1rem 0">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:6px 12px;border-bottom:1px solid var(--border,#e5e5e5);font-size:12px;color:var(--muted-foreground,#6b7280)">
        <span style="display:inline-flex;align-items:center;gap:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${FILE_CODE_SVG}${label ?? "HTML embed"}</span>
        <span style="display:inline-flex;align-items:center;gap:6px"><a href="${src}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;color:inherit">${EXTERNAL_SVG}</a>${fsBtn}</span>
      </div>
      <iframe src="${src}" title="${label ?? "HTML embed"}" style="height:${h}px;width:100%;display:block;border:0;background:#fff" sandbox="${embedSandbox(parsed.src)}"></iframe>
    </div>`;
  }

  if (parsed.html) {
    const h = parsed.height ?? 520;
    return `<div ${wrapAttrs} style="border-radius:8px;overflow:hidden;border:1px solid var(--border,#e5e5e5);margin:1rem 0">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:6px 12px;border-bottom:1px solid var(--border,#e5e5e5);font-size:12px;color:var(--muted-foreground,#6b7280)">
        <span style="display:inline-flex;align-items:center;gap:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${FILE_CODE_SVG}${label ?? "HTML embed"}</span>
        <span style="display:inline-flex;align-items:center;gap:6px">${fsBtn}</span>
      </div>
      <iframe srcdoc="${escapeHtml(parsed.html)}" title="${label ?? "HTML embed"}" style="height:${h}px;width:100%;display:block;border:0;background:#fff" sandbox="allow-scripts allow-forms allow-popups"></iframe>
    </div>`;
  }

  return "";
}

export function transformHtmlEmbeds(html: string): string {
  return html.replace(
    /<pre><code class="[^"]*html-embed[^"]*">([\s\S]*?)<\/code><\/pre>/g,
    (_match, content: string) => {
      const decoded = content
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      const parsed = parseDirective(decoded.trim());
      if (!parsed.src && !parsed.html) return _match;
      return buildIframeHtml(parsed);
    },
  );
}
